import React from "react";
import useSWR from "swr";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { fetcher, fmtN } from "./common";

/** Rapprochement bancaire : pointage des écritures de trésorerie contre le relevé. */
export default function Reconciliation({ file }) {
  const [account, setAccount] = React.useState("521");
  const [statement, setStatement] = React.useState("");
  const key = `/accounting/files/${file.id}/reconciliation?account=${account}`;
  const { data, mutate: reload } = useSWR(key, fetcher);
  const { data: accounts = [] } = useSWR(`/accounting/files/${file.id}/accounts`, fetcher);
  const treasuryAccounts = accounts.filter((a) => a.code.startsWith("5"));

  const toggle = async (row) => {
    try {
      await api.put(`/accounting/files/${file.id}/reconcile`, { entry_ids: [row.entry_id], reconciled: !row.reconciled });
      reload();
    } catch (err) { toast.error(err?.response?.data?.detail || "Pointage impossible"); }
  };

  const stmtVal = parseFloat(statement);
  const ecart = !isNaN(stmtVal) && data ? Math.round((stmtVal - data.solde_pointe) * 100) / 100 : null;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-3">
        <div>
          <Label>Compte de trésorerie</Label>
          <Select value={account} onValueChange={setAccount}>
            <SelectTrigger className="w-72" data-testid="recon-account"><SelectValue /></SelectTrigger>
            <SelectContent>
              {treasuryAccounts.map((a) => (<SelectItem key={a.code} value={a.code}>{a.code} — {a.name}</SelectItem>))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Solde du relevé bancaire</Label>
          <Input type="number" step="0.01" value={statement} onChange={(e) => setStatement(e.target.value)} className="w-48" data-testid="recon-statement" placeholder="Solde au relevé" />
        </div>
        {ecart !== null && (
          <div className={`text-xs px-3 py-2 rounded-md ${Math.abs(ecart) < 0.01 ? "bg-emerald-50 text-emerald-800" : "bg-red-50 text-red-800"}`} data-testid="recon-ecart">
            {Math.abs(ecart) < 0.01 ? "Rapproché ✓" : `Écart : ${fmtN(ecart)} ${file.currency}`}
          </div>
        )}
      </div>

      <div className="border border-zinc-200 bg-white rounded-md overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-zinc-50 border-b border-zinc-200">
            <tr className="text-left text-[10px] uppercase tracking-[0.18em] text-zinc-500">
              <th className="px-3 py-2 w-10">Pointé</th><th className="px-3 py-2">Date</th><th className="px-3 py-2">Pièce</th>
              <th className="px-3 py-2">Libellé</th><th className="px-3 py-2 text-right">Débit</th><th className="px-3 py-2 text-right">Crédit</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-100">
            {data && data.rows.length === 0 && <tr><td colSpan={6} className="p-6 text-zinc-500">Aucune écriture validée sur ce compte.</td></tr>}
            {(data?.rows || []).map((r) => (
              <tr key={r.entry_id} className={r.reconciled ? "bg-emerald-50/40" : ""} data-testid={`recon-row-${r.entry_id}`}>
                <td className="px-3 py-2"><Checkbox checked={r.reconciled} onCheckedChange={() => toggle(r)} data-testid={`recon-check-${r.entry_id}`} /></td>
                <td className="px-3 py-2">{r.date}</td>
                <td className="px-3 py-2 font-mono text-xs">{r.piece}</td>
                <td className="px-3 py-2 truncate max-w-[260px]">{r.label || "—"}</td>
                <td className="px-3 py-2 text-right">{r.amount > 0 ? fmtN(r.amount) : ""}</td>
                <td className="px-3 py-2 text-right">{r.amount < 0 ? fmtN(-r.amount) : ""}</td>
              </tr>
            ))}
          </tbody>
          {data && (
            <tfoot className="bg-zinc-50 border-t border-zinc-200 text-sm">
              <tr>
                <td colSpan={4} className="px-3 py-2 text-zinc-500">Solde comptable : <b>{fmtN(data.solde_comptable)}</b> • Pointé : <b>{fmtN(data.solde_pointe)}</b> • Non pointé : <b>{fmtN(data.solde_non_pointe)}</b></td>
                <td className="px-3 py-2 text-right font-medium" data-testid="recon-total-debit">{fmtN(data.rows.filter((r) => r.amount > 0).reduce((s, r) => s + r.amount, 0))}</td>
                <td className="px-3 py-2 text-right font-medium" data-testid="recon-total-credit">{fmtN(-data.rows.filter((r) => r.amount < 0).reduce((s, r) => s + r.amount, 0))}</td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
      <p className="text-xs text-zinc-500">Pointez chaque ligne présente sur le relevé : le solde pointé doit égaler le solde du relevé. Le solde non pointé correspond aux opérations comptabilisées non encore passées en banque (chèques non encaissés, virements en cours…).</p>
    </div>
  );
}
